import { createContext, useContext } from "react";
import { DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS } from "@verity/proctoring-option";
import { PortalAuthenticationManager } from "./portal-authentication-manager";
import { CandidatePageKey, PortalContext, PortalPageInfo, PortalPageKey, UserPortalContext } from "./portal-config";
import { CourseInfo, DEFAULT_VERITY_INSTITUTE_SETTINGS, VerityInstituteSettings } from "@verity/components";
import { AssessmentInfo, InstituteSettings, PlatformInfo, CourseSettings } from "@verity/api-client/types";
import { UserInfo } from "../types/general-types";
import { BrowserType } from "../helpers/device";
import { UserPrivileges } from "../helpers/privilege";

const PortalAuthenticationManagerContext = createContext({} as PortalAuthenticationManager);
export default PortalAuthenticationManagerContext;

export type PortalContextUpdate = {
	courseId?: number | null;
	assessmentId?: number | null;
};

export type PortalUserInfo = {
	id?: number;
	uuid?: string;
	userInfo?: UserInfo;
};

export type ApiIntegrationInfo = {
	course?: {
		edit: boolean;
	};
	institute?: {
		edit: boolean;
	};
};

type PortalAction = "createPracticeQuiz" | "syncLmsAccounts" | "courseScopeTest" | "instituteScopeTest";

export type PortalUiElement = "introVideo" | "visualizerBanner" | "quickSetupGuide";

export interface PortalController {
	topFrame: boolean;

	initialPortalContext: PortalContext;

	userPlatformInfo: PlatformInfo;

	browserType: BrowserType;

	verityInstituteSettings: VerityInstituteSettings;

	instituteSettings: InstituteSettings;

	courseInfo?: CourseInfo;

	courseSettings: CourseSettings;

	userInfo?: PortalUserInfo;

	userPrivileges: UserPrivileges;

	apiIntegrations: ApiIntegrationInfo;

	fetchApiIntegrations(): void;

	pages: Record<string, PortalPageInfo>;

	currPage?: PortalPageKey | CandidatePageKey;

	action?: PortalAction;

	setAction?: (action?: PortalAction) => void;

	navigateToPage(pageKey: PortalPageKey | CandidatePageKey, state?: unknown): void;

	portalContext: UserPortalContext;

	reportContext: UserPortalContext;

	changePortalContext(context: PortalContextUpdate): Promise<void>;

	returnToAssessmentInfo?: {
		url: string;
		assessmentInfo?: Partial<AssessmentInfo>;
	};

	openQuickSetupGuide(shouldNavigate?: boolean): void;

	visualizerDetected: boolean | undefined;

	lti1p3Active: boolean | undefined;

	canSwitchCourse: boolean;

	dismissableUiElements: Set<PortalUiElement>;

	dismissUiElement: (item: PortalUiElement) => void;
}

export const DEFAULT_INSTITUTE_SETTINGS: InstituteSettings = {
	...DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS,
	suffix: null,
	managedGuestSessionRequireIncognito: true,
	recordingMaxAgeMonths: 12,
};

export const DEFAULT_COURSE_SETTINGS: CourseSettings = {
	geofenceLocations: [],
};

export const defaultHomeOrigin = window.location.origin;

const initialPortalContext = { homeOrigin: defaultHomeOrigin };

export const PortalControllerContext = createContext<PortalController>({
	topFrame: false,
	initialPortalContext,
	userPlatformInfo: { platform: "none", versionSupported: false },
	browserType: "desktop-browser",
	verityInstituteSettings: DEFAULT_VERITY_INSTITUTE_SETTINGS,
	instituteSettings: DEFAULT_INSTITUTE_SETTINGS,
	courseSettings: DEFAULT_COURSE_SETTINGS,
	userPrivileges: {},
	apiIntegrations: {},
	fetchApiIntegrations: () => {},
	pages: {},
	navigateToPage: () => {},
	portalContext: initialPortalContext,
	reportContext: initialPortalContext,
	changePortalContext: async () => {},
	openQuickSetupGuide: () => {},
	visualizerDetected: undefined,
	lti1p3Active: undefined,
	canSwitchCourse: false,
	dismissableUiElements: new Set(),
	dismissUiElement: () => {},
});

export const usePortalStateContext = () => useContext(PortalControllerContext);
